import {Inject, Injectable, OnDestroy} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';

import {ReplaySubject, of} from 'rxjs';
import {
  catchError,
  concatMap,
  filter,
  takeUntil,
  skip,
  switchMap,
  take,
} from 'rxjs/operators';

import * as APIM from '@common/utils/dist/models/api-messages.js';
import * as PL2 from '@common/utils/dist/models/models-pl2.js';
import {UtilsPL2 as U} from '@common/utils/dist/utils/utils-pl2.js';
import * as UU from '@common/utils/dist/utils/user-utils-pl2.js';
import {isStillNew} from '@common/utils/dist/utils/date-utils.js';

import {UserDetailsStore} from '@stores/user-details.store';
import {UserStore} from '@stores/user.store';
import {
  EventAPIService,
  EventAPIServiceToken,
} from '@services/event-api.service';
import {UserDetailsUpsertEventInputBuilder} from '@services/events/user-details-events';
import {RouteStore} from '@stores/route.store';
import {NotificationStore} from '@stores/notification.store';
import {explorePageName} from '@constants/app.config';
import {CloudlabCompleteProfileDialogComponent} from '@components/complete-profile/cloudlab-complete-profile-dialog.component';
import {ExploreSnackbarPreferences} from '@services/preferences/explore-snackbar.preferences';

@Injectable({
  providedIn: 'root',
})
export class UserDetailsReactor implements OnDestroy {
  private _destroyed$ = new ReplaySubject<boolean>();

  private _previousState: Partial<PL2.UserDetails> = {uAt: '0'};

  constructor(
    private userDetailsStore: UserDetailsStore,
    private userStore: UserStore,
    @Inject(EventAPIServiceToken) private eventApiService: EventAPIService,
    private dialog: MatDialog,
    private routeStore: RouteStore,
    private notificationStore: NotificationStore,
    private snackbarPrefs: ExploreSnackbarPreferences,
  ) {
    this.userDetailsStore.debounceState$
      .pipe(
        filter(
          () =>
            !U.isEmpty(this.userStore.state()?.userId) &&
            this._stateUpdated(this.userDetailsStore.state()),
        ),
        concatMap((details) => {
          const user = this.userStore.state();
          const payload = {
            ...U.exclude(details, 'recentLessons', 'uAt'),
            userId: user.userId,
            preferedId: UU.userDetailsPreferedId(user),
            email: user.email,
          } as Partial<PL2.UserDetails> & APIM.UserDetailsKey;
          const event = new UserDetailsUpsertEventInputBuilder(payload);
          this._previousState = details;
          return this.eventApiService.sendEvent(event).pipe(
            catchError((err) => {
              console.error('[UserDetailsReactor]', err);
              return of(void 0);
            }),
          );
        }),
        takeUntil(this._destroyed$),
      )
      .subscribe();

    this.userDetailsStore.debounceState$
      .pipe(
        filter((details) => !U.isEmpty(details) && details.isDone !== true),
        filter((details) => !isStillNew(details.createdAt, 30)),
        switchMap(() => this.routeStore.state$),
        filter((routeState) => routeState.pageName === explorePageName),
        filter(() => !this.snackbarPrefs.completeProfileDismissed),
        filter(() => UU.isTeacher(this.userStore.state())),
        takeUntil(this._destroyed$),
      )
      .subscribe(() => {
        this.notificationStore.showSnackbar(
          'explore.completeProfile',
          {
            action: {
              key: 'explore.completeProfileAction',
              handler: () => {
                this.snackbarPrefs.completeProfileDismissed = true;
                CloudlabCompleteProfileDialogComponent.open(this.dialog);
              },
            },
            onDismiss: () => {
              this.snackbarPrefs.completeProfileDismissed = true;
            },
          },
          'complete-profile',
        );
      });

    this.userDetailsStore.state$
      .pipe(
        skip(1), // skip initial empty value
        filter(
          (userDetails) =>
            !U.isEmpty(userDetails?.createdAt) &&
            isStillNew(userDetails.createdAt, 30),
        ),
        filter(() => UU.isTeacher(this.userStore.state())),
        take(1),
        takeUntil(this._destroyed$),
      )
      .subscribe(() => {
        this.snackbarPrefs.completeProfileDismissed = true;
        CloudlabCompleteProfileDialogComponent.open(this.dialog);
      });
  }

  ngOnDestroy() {
    this._destroyed$.next(true);
    this._destroyed$.complete();
  }

  private _stateUpdated(newState: Partial<PL2.UserDetails>): boolean {
    if (U.isEmpty(this._previousState?.uAt)) {
      return true;
    }
    return newState?.uAt > this._previousState?.uAt;
  }
}
